import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { Toast } from '#components/toast/toast.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { DatasheetPopoverDocumentPortal } from './datasheet-popover-document-portal.js';
import { useDatasheetPopoverStore } from './datasheet-popover-store.js';
import { DatasheetPopoverWindow } from './datasheet-popover-window.js';
import { ExternalWindowPortal } from './external-window-portal.js';

import type { DatasheetPopoverTarget } from './datasheet-popover-store.js';

/**
 * Renders the app's singleton datasheet popover plus any independent external-window copies.
 */
export function DatasheetPopover() {
	const target = useDatasheetPopoverStore((state) => state.target);
	const targetDocument = useDatasheetPopoverStore((state) => state.targetDocument);
	const close = useDatasheetPopoverStore((state) => state.close);
	const externalWindows = useDatasheetPopoverStore((state) => state.externalWindows);
	const openExternal = useDatasheetPopoverStore((state) => state.openExternal);
	const closeExternal = useDatasheetPopoverStore((state) => state.closeExternal);
	const addToast = useAddToast();

	const registerExternalWindow = useCallback(
		(target: DatasheetPopoverTarget, externalWindow: Window | null) => {
			if (externalWindow === null) {
				addToast(
					<Toast severity='error' title='Could not open datasheet window'>
						<p>Allow pop-ups for this site and try again.</p>
					</Toast>,
				);
				return;
			}

			openExternal(target, externalWindow);
			close();
		},
		[addToast, close, openExternal],
	);

	// The popover is tied to the design view it was opened from, so close it on navigation.
	const pathname = useRouterState({ select: (state) => state.location.pathname });
	useEffect(() => {
		close();
	}, [pathname, close]);

	return (
		<>
			{target && (
				// Key on fileId so the in-app window (and its workbench) fully resets when a different datasheet is opened.
				<DatasheetPopoverDocumentPortal targetDocument={targetDocument} onTargetDocumentClose={close}>
					<DatasheetPopoverWindow
						key={target.fileId}
						target={target}
						variant='floating'
						onClose={close}
						onPopOut={(externalWindow, poppedOutTarget) => registerExternalWindow(poppedOutTarget, externalWindow)}
					/>
				</DatasheetPopoverDocumentPortal>
			)}

			{externalWindows.map((windowRecord) => (
				<ExternalWindowPortal
					key={windowRecord.id}
					externalWindow={windowRecord.externalWindow}
					title={`Datasheet ${windowRecord.target.mpn}`}
					onClose={() => closeExternal(windowRecord.id)}
				>
					<DatasheetPopoverWindow
						target={windowRecord.target}
						variant='external'
						onClose={() => closeExternal(windowRecord.id)}
					/>
				</ExternalWindowPortal>
			))}
		</>
	);
}
